import { useEffect, useMemo, useState } from "react";
import { FaFileExcel, FaDownload, FaCalendarDay, FaHistory, FaExclamationTriangle } from "react-icons/fa";
import moment from "moment-timezone";
import {
  useGetDevicesAtDateReportMutation,
  useGetFirmDeviceHistoryReportMutation,
} from "../../../store/storageRoles/reportsApi";
import useAuth from "../../../Hooks/useAuth";
import useReportFirms from "../../../Hooks/useReportFirms";
import ReportLayout from "../../../Layout/Reports/ReportLayout";
import ReportFilterCard from "../../../Components/Reports/ReportFilterCard";
import ReportLoadingState from "../../../Components/Reports/ReportLoadingState";
import ReportErrorState from "../../../Components/Reports/ReportErrorState";
import ReportEmptyState from "../../../Components/Reports/ReportEmptyState";
import ReportSheetsPreview from "../../../Components/Reports/ReportSheetsPreview";
import { FilterTabs } from "../../../Components/shared";
import { CustomDropdown, DateField, FirmDropdown } from "../../../Components/atoms";
import { downloadSheetsAsXlsx } from "../../../utils/reportSheetsExcel";
import { PRODUCT_OPTIONS } from "../Firm/performanceReportOptions";

/**
 * TARİHTEKİ MAKİNELER (#356) — iki sekme:
 *  - Tarihe Göre: seçilen günün sonunda makineler (toplam / merkez / bayi /
 *    müşteri), firma ve makine dökümü. Firma seçilirse o firmanın ağı.
 *  - Müşteri Geçmişi: firmanın makine stint'leri (geliş–gidiş, tüketim) ve
 *    çalışma dönemleri (ara verme süresi dahil).
 * Sunucu yalnız `sheets` döner (PDF yok); önizleme ve indirme Excel.
 * Sözleşme: BayinerIOT-Workspace-/docs/api-contracts/devices-at-date-report.md
 */

const MODE_DATE = "date";
const MODE_HISTORY = "history";
const MODE_TABS = [
  { id: MODE_DATE, label: "Tarihe Göre", icon: FaCalendarDay },
  { id: MODE_HISTORY, label: "Müşteri Geçmişi", icon: FaHistory },
];
const ALL_PRODUCTS = { id: "all", label: "Tüm Ürünler", value: "" };
const toDay = (d) => moment(d).format("YYYY-MM-DD");
const trDay = (iso) => (iso ? moment(iso, "YYYY-MM-DD").format("DD.MM.YYYY") : "");

export default function DevicesAtDateReportPage() {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const { firms, isLoading: firmsLoading } = useReportFirms(token);

  const [mode, setMode] = useState(MODE_DATE);
  const [date, setDate] = useState(() => new Date());
  const [scopeFirm, setScopeFirm] = useState(null);
  const [historyFirm, setHistoryFirm] = useState(null);
  const [product, setProduct] = useState(ALL_PRODUCTS);

  const [getAtDate, atDate] = useGetDevicesAtDateReportMutation();
  const [getHistory, history] = useGetFirmDeviceHistoryReportMutation();
  const active = mode === MODE_DATE ? atDate : history;
  const report = active.data?.data;
  const { isLoading, error } = active;

  // Sekme değişince eski sonuç başka sekmenin filtresiyle karışmasın
  useEffect(() => {
    atDate.reset?.();
    history.reset?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Espresso türev adlarla (Ristretto, Cappuccino…) kaydolur, uç tam ad
  // eşler — tek tek seçilmez; "Tüm Ürünler"de türev adlarıyla görünür.
  const productOptions = useMemo(
    () => [ALL_PRODUCTS, ...PRODUCT_OPTIONS.filter((o) => o.value !== "Espresso")],
    [],
  );
  const scopeFirmId = scopeFirm?._id && scopeFirm._id !== "all" ? scopeFirm._id : null;
  const canSearch = mode === MODE_DATE ? Boolean(date) : Boolean(historyFirm?._id);

  const handleSearch = () => {
    if (!canSearch) return;
    if (mode === MODE_DATE) {
      getAtDate({ token, date: toDay(date), ...(scopeFirmId ? { firmId: scopeFirmId } : {}) });
    } else {
      getHistory({
        token,
        firmId: historyFirm._id,
        ...(product?.value ? { productName: product.value } : {}),
      });
    }
  };

  const handleClear = () => {
    setDate(new Date());
    setScopeFirm(null);
    setHistoryFirm(null);
    setProduct(ALL_PRODUCTS);
    active.reset?.();
  };

  // Taşıma kayıtlarından eski tarih: sonuç bugünkü yere dayanır (sözleşme)
  const beforeHistory =
    report?.historyStart &&
    (mode === MODE_DATE ? report.date < report.historyStart : report.stints?.some((s) => s.source === "consumption"));

  const fileName =
    mode === MODE_DATE
      ? `tarihteki-makineler-${report?.date || ""}`
      : `makine-gecmisi-${(report?.firm?.firmName || "firma").replace(/\s+/g, "-")}`;

  const headerActions =
    report?.sheets?.length > 0 ? (
      <button
        type="button"
        onClick={() => downloadSheetsAsXlsx(report.sheets, fileName)}
        title="Excel İndir"
        className="flex items-center gap-1.5 px-2.5 py-2 sm:px-3.5 bg-emerald-700 text-white rounded-lg shadow-sm hover:bg-emerald-800 active:bg-emerald-900 transition-colors text-xs font-SemiBold"
      >
        <FaFileExcel className="w-4 h-4" />
        <FaDownload className="w-3 h-3" />
        <span className="hidden sm:inline">Excel İndir</span>
      </button>
    ) : null;

  const activeFilters =
    mode === MODE_DATE
      ? [
          { label: "Tarih", value: date ? new Date(date).toLocaleDateString("tr-TR") : "—" },
          { label: "Firma", value: scopeFirmId ? scopeFirm.firmName : "Tüm Firmalar" },
        ]
      : [
          { label: "Firma", value: historyFirm?.firmName || "—" },
          { label: "Ürün", value: product?.label || "Tüm Ürünler" },
        ];

  const errorMessage = error?.data?.message || error?.error || "Rapor oluşturulurken bir hata oluştu.";

  return (
    <ReportLayout
      title="Tarihteki Makineler"
      showExcel={false}
      headerActions={headerActions}
      sidebar={
        <ReportFilterCard
          title="Filtre Seçenekleri"
          description={
            mode === MODE_DATE
              ? "Seçilen günün sonunda kaç makinenin nerede olduğunu gösterir"
              : "Firmayla hangi dönemlerde hangi makinelerle çalışıldığını gösterir"
          }
          showDatePickers={false}
          onSearch={handleSearch}
          onClear={handleClear}
          isLoading={isLoading}
          searchDisabled={!canSearch}
          searchDisabledMessage={mode === MODE_DATE ? "Tarih seçin" : "Firma seçin"}
          searchButtonText="Raporu Getir"
          clearButtonText="Temizle"
          activeFilters={activeFilters}
        >
          <div className="grid grid-cols-1 gap-4">
            <FilterTabs filters={MODE_TABS} activeFilter={mode} onChange={setMode} color="indigo" />

            {mode === MODE_DATE ? (
              <>
                <DateField label="Tarih" required value={date} onChange={(d) => setDate(d)} disableFuture />
                <FirmDropdown
                  label="Firma (opsiyonel)"
                  value={scopeFirm}
                  onChange={setScopeFirm}
                  firms={firms}
                  loading={firmsLoading}
                  placeholder="Tüm Firmalar"
                />
              </>
            ) : (
              <>
                <FirmDropdown
                  label="Müşteri / Bayi"
                  required
                  value={historyFirm}
                  onChange={setHistoryFirm}
                  firms={firms}
                  loading={firmsLoading}
                  showAllOption={false}
                  placeholder="Firma seçin"
                />
                <CustomDropdown
                  label="Ürün"
                  value={product}
                  onChange={setProduct}
                  options={productOptions}
                  showSearch={false}
                />
              </>
            )}
          </div>
        </ReportFilterCard>
      }
    >
      {isLoading && (
        <ReportLoadingState
          title="Rapor Hazırlanıyor"
          description="Taşıma ve tüketim kayıtları taranıyor, lütfen bekleyin..."
          progressText="Veriler işleniyor..."
          skeletonRows={6}
          skeletonColumns={4}
        />
      )}

      {!isLoading && error && <ReportErrorState title="Hata Oluştu" message={errorMessage} icon="warning" />}

      {!isLoading && !error && report && (
        <div className="flex-1 min-h-0 flex flex-col gap-3">
          {beforeHistory && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <FaExclamationTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Taşıma kayıtları {trDay(report.historyStart)} tarihinde başlıyor. Bu tarihten önceki makine
                yerleri, makinelerin bilinen ilk konumuna dayanır
                {mode === MODE_HISTORY ? "; \"Tüketim kaydı\" kaynaklı satırlar yalnız tüketimden çıkarıldı" : ""}.
              </span>
            </div>
          )}
          <ReportSheetsPreview sheets={report.sheets} />
        </div>
      )}

      {!isLoading && !error && !report && (
        <ReportEmptyState
          title="Rapor Bekleniyor"
          message="Seçimleri yapıp 'Raporu Getir' butonuna basın."
          icon="document"
        />
      )}
    </ReportLayout>
  );
}
